import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { theme } from '../styles/theme';
import Button from './ui/Button';
import LimitReachedBanner from './LimitReachedBanner';
import { FaLink, FaCopy, FaCheckCircle, FaDownload } from 'react-icons/fa';

interface TrackableLinksViewProps {
  orgId: string;
  onBack: () => void;
  refreshTrigger?: number;
  linkCount?: number;
  linkLimit?: number;
}

interface TrackableLink {
  id: number;
  orgId: string;
  smLink: string;
  destinationUrl: string;
  trackableLink: string;
  uid: string;
  ogDescription: string | null;
  dateCreated: string;
}

const TrackableLinksView: React.FC<TrackableLinksViewProps> = ({
  orgId,
  onBack,
  refreshTrigger = 0,
  linkCount = 0,
  linkLimit = 25
}) => {
  const [links, setLinks] = useState<TrackableLink[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedLinkId, setCopiedLinkId] = useState<number | null>(null);

  useEffect(() => {
    fetchLinks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, refreshTrigger]);

  const fetchLinks = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.trackableLinks.fetchByOrgId(orgId);

      if (response.success) {
        setLinks(response.trackableLinks || []);
      } else {
        setError(response.error || 'Failed to fetch trackable links');
      }
    } catch (err: any) {
      console.error('Error fetching trackable links:', err);
      setError(err.message || 'Failed to fetch trackable links');
    } finally {
      setLoading(false);
    }
  };

  const downloadCsv = () => {
    const escape = (value: string) => `"${(value ?? '').replace(/"/g, '""')}"`;
    const header = ['Social Media Link', 'Destination URL', 'Trackable Link'];
    const rows = links.map((link) => [
      escape(link.smLink),
      escape(link.destinationUrl),
      escape(link.trackableLink),
    ].join(','));
    const csv = [header.join(','), ...rows].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const date = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `trackable-links-${orgId}-${date}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = async (trackableLink: string, linkId: number) => {
    try {
      await navigator.clipboard.writeText(trackableLink);
      setCopiedLinkId(linkId);
      setTimeout(() => setCopiedLinkId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const truncateUrl = (url: string, maxLength: number = 40) => {
    if (url.length <= maxLength) return url;
    return url.substring(0, maxLength) + '...';
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (err) {
      return dateString;
    }
  };

  return (
    <div>
      {/* Limit Reached Banner */}
      <LimitReachedBanner linkCount={linkCount} linkLimit={linkLimit} />

      <div style={{
        padding: '24px',
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '32px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Button
              variant="secondary"
              size="sm"
              onClick={onBack}
            >
              ← Back
            </Button>
            <h1 style={{
              margin: 0,
              fontSize: theme.typography.sizes['2xl'],
              fontWeight: theme.typography.weights.bold,
              color: theme.colors.text.primary
            }}>
              Trackable Links
            </h1>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              fontSize: theme.typography.sizes.base,
              color: theme.colors.text.secondary
            }}>
              {links.length} {links.length === 1 ? 'link' : 'links'}
            </div>
            {orgId === 'A44366' && links.length > 0 && (
              <Button
                variant="secondary"
                size="sm"
                onClick={downloadCsv}
              >
                {React.createElement(FaDownload as React.ComponentType<any>, { size: 12, style: { marginRight: '6px' } })}
                Download CSV
              </Button>
            )}
          </div>
        </div>

      {/* Loading State */}
      {loading && (
        <div style={{
          textAlign: 'center',
          padding: '60px 0',
          color: theme.colors.text.secondary
        }}>
          Loading trackable links...
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div style={{
          padding: '20px',
          backgroundColor: '#FEF2F2',
          border: '1px solid #FCA5A5',
          borderRadius: '8px',
          color: '#DC2626',
          marginBottom: '24px'
        }}>
          {error}
          <Button
            variant="secondary"
            size="sm"
            onClick={fetchLinks}
            style={{ marginTop: '12px' }}
          >
            Retry
          </Button>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && links.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '80px 20px',
          color: theme.colors.text.secondary
        }}>
          <div style={{ marginBottom: '16px' }}>
            {React.createElement(FaLink as React.ComponentType<any>, { size: 64, color: theme.colors.text.secondary })}
          </div>
          <p style={{
            margin: 0,
            fontSize: theme.typography.sizes.lg,
            fontWeight: theme.typography.weights.medium
          }}>
            No trackable links yet
          </p>
          <p style={{
            margin: '8px 0 0 0',
            fontSize: theme.typography.sizes.base,
            color: theme.colors.text.secondary
          }}>
            Create your first link using the "Create Link" button in the navbar
          </p>
        </div>
      )}

      {/* Table */}
      {!loading && !error && links.length > 0 && (
        <div style={{
          backgroundColor: theme.colors.surface,
          borderRadius: '8px',
          border: `1px solid ${theme.colors.border}`,
          overflowX: 'auto'
        }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: theme.typography.sizes.sm
          }}>
            <thead>
              <tr style={{
                backgroundColor: theme.colors.background,
                borderBottom: `2px solid ${theme.colors.border}`
              }}>
                <th style={{
                  padding: '12px',
                  textAlign: 'left',
                  fontWeight: theme.typography.weights.semibold,
                  color: theme.colors.text.primary,
                  width: '25%'
                }}>
                  Social Media Link
                </th>
                <th style={{
                  padding: '12px',
                  textAlign: 'left',
                  fontWeight: theme.typography.weights.semibold,
                  color: theme.colors.text.primary,
                  width: '25%'
                }}>
                  Destination URL
                </th>
                <th style={{
                  padding: '12px',
                  textAlign: 'left',
                  fontWeight: theme.typography.weights.semibold,
                  color: theme.colors.text.primary,
                  width: '20%'
                }}>
                  Offer
                </th>
                <th style={{
                  padding: '12px',
                  textAlign: 'left',
                  fontWeight: theme.typography.weights.semibold,
                  color: theme.colors.text.primary,
                  width: '15%',
                  whiteSpace: 'nowrap'
                }}>
                  Date Created
                </th>
                <th style={{
                  padding: '12px',
                  textAlign: 'center',
                  fontWeight: theme.typography.weights.semibold,
                  color: theme.colors.text.primary,
                  width: '15%',
                  whiteSpace: 'nowrap'
                }}>
                  Generated Link
                </th>
              </tr>
            </thead>
            <tbody>
              {links.map((link, index) => (
                <tr
                  key={link.id}
                  style={{
                    borderBottom: index < links.length - 1 ? `1px solid ${theme.colors.border}` : 'none',
                    transition: 'background-color 0.15s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = theme.colors.background;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <td style={{
                    padding: '12px',
                    verticalAlign: 'middle',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '200px'
                  }}>
                    <a
                      href={link.smLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: theme.colors.primary,
                        textDecoration: 'none'
                      }}
                      title={link.smLink}
                    >
                      {truncateUrl(link.smLink)}
                    </a>
                  </td>
                  <td style={{
                    padding: '12px',
                    verticalAlign: 'middle',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '200px'
                  }}>
                    <a
                      href={link.destinationUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: theme.colors.primary,
                        textDecoration: 'none'
                      }}
                      title={link.destinationUrl}
                    >
                      {truncateUrl(link.destinationUrl)}
                    </a>
                  </td>
                  <td style={{
                    padding: '12px',
                    verticalAlign: 'middle',
                    color: theme.colors.text.secondary,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '150px'
                  }}>
                    {link.ogDescription || '-'}
                  </td>
                  <td style={{
                    padding: '12px',
                    verticalAlign: 'middle',
                    color: theme.colors.text.secondary,
                    whiteSpace: 'nowrap',
                    fontSize: theme.typography.sizes.xs
                  }}>
                    {formatDate(link.dateCreated)}
                  </td>
                  <td style={{
                    padding: '12px',
                    textAlign: 'center',
                    verticalAlign: 'middle'
                  }}>
                    <button
                      onClick={() => copyToClipboard(link.trackableLink, link.id)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: copiedLinkId === link.id ? theme.colors.accent : theme.colors.primary,
                        color: '#FFFFFF',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: theme.typography.sizes.xs,
                        fontWeight: theme.typography.weights.medium,
                        transition: 'all 0.2s ease',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {copiedLinkId === link.id ? (
                        <>
                          {React.createElement(FaCheckCircle as React.ComponentType<any>, { size: 12 })}
                          <span>Copied</span>
                        </>
                      ) : (
                        <>
                          {React.createElement(FaCopy as React.ComponentType<any>, { size: 12 })}
                          <span>Copy</span>
                        </>
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </div>
    </div>
  );
};

export default TrackableLinksView;
